<template>
    <BaseCard padding="lg" as="section">
        <div class="flex flex-col gap-4">
            <div class="grid gap-4 md:grid-cols-2">
                <label :for="`${uid}-name`" class="block">
                    <span class="block body-small-bold text-valence-grey-850 mb-2">Survey name</span>
                    <input
                        :id="`${uid}-name`"
                        :value="template.name"
                        type="text"
                        maxlength="255"
                        placeholder="e.g. Leadership 360"
                        class="w-full px-3 py-2 border border-valence-grey-200 rounded-lg outline-none focus:border-valence-primary-300 body-small"
                        @input="$emit('update', { name: ($event.target as HTMLInputElement).value })" />
                </label>

                <label :for="`${uid}-code`" class="block">
                    <span class="block body-small-bold text-valence-grey-850 mb-2">Code</span>
                    <input
                        :id="`${uid}-code`"
                        :value="template.template_code"
                        type="text"
                        maxlength="64"
                        placeholder="e.g. leadership_360"
                        class="w-full px-3 py-2 border border-valence-grey-200 rounded-lg outline-none focus:border-valence-primary-300 body-small"
                        @input="$emit('update', { template_code: ($event.target as HTMLInputElement).value })" />
                    <span class="block details text-valence-grey-750 mt-1">Stable identifier used by campaigns and reports.</span>
                </label>

                <label :for="`${uid}-scale`" class="block">
                    <span class="block body-small-bold text-valence-grey-850 mb-2">Default scale</span>
                    <select
                        :id="`${uid}-scale`"
                        :value="template.default_scale"
                        class="w-full px-3 py-2 border border-valence-grey-200 rounded-lg outline-none focus:border-valence-primary-300 body-small"
                        @change="$emit('update', { default_scale: ($event.target as HTMLSelectElement).value })">
                        <option v-for="scale in choices.scales" :key="scale.value" :value="scale.value">{{ scale.label }}</option>
                    </select>
                </label>

                <label :for="`${uid}-layout`" class="block">
                    <span class="block body-small-bold text-valence-grey-850 mb-2">Layout</span>
                    <select
                        :id="`${uid}-layout`"
                        :value="template.display_mode"
                        class="w-full px-3 py-2 border border-valence-grey-200 rounded-lg outline-none focus:border-valence-primary-300 body-small"
                        @change="$emit('update', { display_mode: ($event.target as HTMLSelectElement).value as DisplayMode })">
                        <option v-for="mode in choices.display_modes" :key="mode.value" :value="mode.value">{{ mode.label }}</option>
                    </select>
                </label>

                <label :for="`${uid}-mode`" class="block">
                    <span class="block body-small-bold text-valence-grey-850 mb-2">Used for</span>
                    <select
                        :id="`${uid}-mode`"
                        :value="template.assessment_mode"
                        class="w-full px-3 py-2 border border-valence-grey-200 rounded-lg outline-none focus:border-valence-primary-300 body-small"
                        @change="$emit('update', { assessment_mode: ($event.target as HTMLSelectElement).value })">
                        <option v-for="mode in choices.assessment_modes" :key="mode.value" :value="mode.value">{{ mode.label }}</option>
                    </select>
                </label>

                <label :for="`${uid}-min-responses`" class="block">
                    <span class="block body-small-bold text-valence-grey-850 mb-2">Minimum rater responses</span>
                    <input
                        :id="`${uid}-min-responses`"
                        :value="template.min_responses"
                        type="number"
                        min="0"
                        max="32767"
                        class="w-32 px-3 py-2 border border-valence-grey-200 rounded-lg outline-none focus:border-valence-primary-300 body-small"
                        @input="$emit('update', { min_responses: Number(($event.target as HTMLInputElement).value) })" />
                    <span class="block details text-valence-grey-750 mt-1">Ignored when relationship thresholds below are set.</span>
                </label>
            </div>

            <fieldset class="flex flex-col gap-3 border-t border-valence-grey-100 pt-4">
                <legend class="body-small-bold text-valence-grey-850">Relationship thresholds</legend>
                <span class="details text-valence-grey-750">
                    Set a per-relationship response threshold. Reports unlock once the subject's self reflection is in and one of these relationships reaches its threshold.
                    Thresholds replace the overall minimum above, and never block launching a 360.
                </span>

                <ul class="flex flex-col gap-2">
                    <li v-for="relationship in choices.rater_relationships" :key="relationship.value" class="flex items-center gap-3">
                        <BaseCheckbox
                            :checkbox-id="`${uid}-threshold-${relationship.value}`"
                            shape="square"
                            :model-value="thresholdFor(relationship.value) !== null"
                            @update:model-value="toggleThreshold(relationship.value, $event === true)">
                            <span class="body-small text-valence-grey-850 w-44 inline-block">{{ relationship.label }}</span>
                        </BaseCheckbox>
                        <input
                            :id="`${uid}-threshold-value-${relationship.value}`"
                            :value="thresholdFor(relationship.value) ?? ''"
                            :disabled="thresholdFor(relationship.value) === null"
                            :aria-label="`${relationship.label} minimum responses`"
                            type="number"
                            min="1"
                            max="32767"
                            class="w-24 px-3 py-2 border border-valence-grey-200 rounded-lg outline-none focus:border-valence-primary-300 body-small disabled:bg-valence-grey-50"
                            @input="setThreshold(relationship.value, Number(($event.target as HTMLInputElement).value))" />
                    </li>
                </ul>
            </fieldset>

            <div class="flex flex-col gap-3">
                <label v-for="toggle in toggles" :key="toggle.field" :for="`${uid}-${toggle.field}`" class="flex items-start gap-3 cursor-pointer">
                    <input
                        :id="`${uid}-${toggle.field}`"
                        :checked="template[toggle.field]"
                        type="checkbox"
                        class="mt-1"
                        @change="$emit('update', { [toggle.field]: ($event.target as HTMLInputElement).checked })" />
                    <span>
                        <span class="block body-small-bold text-valence-grey-850">{{ toggle.label }}</span>
                        <span class="block details text-valence-grey-750">{{ toggle.hint }}</span>
                    </span>
                </label>

                <div v-if="!template.is_global">
                    <label :for="`${uid}-org-search`" class="block">
                        <span class="flex items-baseline justify-between mb-2">
                            <span class="body-small-bold text-valence-grey-850">Organizations</span>
                            <span class="details text-valence-grey-750">{{ template.organization_ids.length }} selected</span>
                        </span>
                        <input
                            :id="`${uid}-org-search`"
                            v-model="organizationSearch"
                            type="search"
                            placeholder="Search organizations"
                            class="w-full px-3 py-2 border border-valence-grey-200 rounded-lg outline-none focus:border-valence-primary-300 body-small" />
                    </label>
                    <ul class="mt-2 max-h-56 overflow-y-auto border border-valence-grey-200 rounded-lg divide-y divide-valence-grey-100">
                        <li v-for="org in matchingOrganizations" :key="org.id" class="px-3 py-2 hover:bg-valence-grey-50">
                            <BaseCheckbox
                                :checkbox-id="`${uid}-org-${org.id}`"
                                shape="square"
                                :model-value="template.organization_ids.includes(org.id)"
                                @update:model-value="toggleOrganization(org.id, $event === true)">
                                <span class="body-small text-valence-grey-850">{{ org.name }}</span>
                            </BaseCheckbox>
                        </li>
                        <li v-if="!matchingOrganizations.length" class="px-3 py-2 body-small text-valence-grey-750">No organizations match “{{ organizationSearch }}”.</li>
                    </ul>
                </div>
            </div>
        </div>
    </BaseCard>
</template>

<script setup lang="ts">
import type { CategoryThreshold } from "~vue/api/generated/reflections";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseCheckbox from "~vue/components/form/BaseCheckbox.vue";
import type { BuilderChoices, BuilderTemplate, DisplayMode } from "~vue/SurveyBuilder/types";
import { computed, ref, useId } from "vue";

type ToggleField = "enabled" | "hide_score_example_composers" | "hide_neutral_response_options" | "is_global";

const props = defineProps<{
    template: BuilderTemplate;
    choices: BuilderChoices;
}>();

const emit = defineEmits<{ update: [changes: Partial<BuilderTemplate>] }>();

const uid = useId();

const toggles: { field: ToggleField; label: string; hint: string }[] = [
    { field: "enabled", label: "Enabled", hint: "Only enabled surveys can be picked when launching a campaign." },
    {
        field: "hide_score_example_composers",
        label: "Hide “share a specific example” on score questions",
        hint: "Raters give a rating only; open-text questions keep their composer.",
    },
    {
        field: "hide_neutral_response_options",
        label: "Hide neutral / “not observed” options",
        hint: "Forces a rating on every score question.",
    },
    { field: "is_global", label: "Available to every organization", hint: "Uncheck to restrict this survey to specific organizations." },
];

const organizationSearch = ref("");

/** Selected organizations stay listed while searching, so a filter can't hide what you're about to unselect. */
const matchingOrganizations = computed(() => {
    const term = organizationSearch.value.trim().toLowerCase();
    if (!term) {
        return props.choices.organizations;
    }
    return props.choices.organizations.filter((org) => org.name.toLowerCase().includes(term) || props.template.organization_ids.includes(org.id));
});

const configuredThresholds = computed<CategoryThreshold[]>(() => props.template.category_thresholds.thresholds ?? []);

function thresholdFor(relationship: string): number | null {
    return configuredThresholds.value.find((threshold) => threshold.value === relationship)?.min_responses ?? null;
}

function emitThresholds(thresholds: CategoryThreshold[]) {
    emit("update", { category_thresholds: { thresholds } });
}

function toggleThreshold(relationship: string, selected: boolean) {
    const current = configuredThresholds.value;
    if (!selected) {
        emitThresholds(current.filter((threshold) => threshold.value !== relationship));
        return;
    }
    // Thresholds are ordered like the relationship choices so the saved config
    // reads the same way as the form.
    const added = [...current, { value: relationship as CategoryThreshold["value"], min_responses: 1 }];
    const order = props.choices.rater_relationships.map((choice) => choice.value);
    emitThresholds(added.sort((left, right) => order.indexOf(left.value) - order.indexOf(right.value)));
}

function setThreshold(relationship: string, minResponses: number) {
    emitThresholds(configuredThresholds.value.map((threshold) => (threshold.value === relationship ? { ...threshold, min_responses: Math.max(1, minResponses) } : threshold)));
}

function toggleOrganization(id: number, selected: boolean) {
    const current = props.template.organization_ids;
    emit("update", { organization_ids: selected ? [...current, id] : current.filter((candidate) => candidate !== id) });
}
</script>
